import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/products/shared/services/supabase/client';
import type { Session, User, Provider } from '@supabase/supabase-js';
import { jwtDecode } from 'jwt-decode';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  accessToken: string | null;
  userRole: string | null;
  tenantId: number | null;
  signIn: (email: string, password: string) => Promise<{
    success: boolean;
    error: string | null;
  }>;
  signInWithAzure: () => Promise<{
    success: boolean;
    error: string | null;
  }>;
  signUpWithAzure: () => Promise<{
    success: boolean;
    error: string | null;
  }>;
  signUp: (email: string, password: string) => Promise<{
    success: boolean;
    error: string | null;
  }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<number | null>(null);

  // Helper to decode JWT and extract claims
  const decodeAndSetJwtClaims = (token: string | null) => {
    if (!token) {
      setUserRole(null);
      setTenantId(null);
      return;
    }

    try {
      const decoded = jwtDecode(token) as { [key: string]: unknown };
      
      // Extract and set user_role
      const role = decoded.user_role as string | null;
      setUserRole(role ?? null);
      
      // Extract and set tenant_id
      const tenant = decoded.tenant_id as number | null;
      setTenantId(tenant ?? null);
      
      // Log JWT claims for debugging multi-tenancy
      console.log('🔐 JWT Claims:', {
        user_role: role,
        tenant_id: tenant,
        sub: decoded.sub,
        email: decoded.email,
        exp: decoded.exp ? new Date((decoded.exp as number) * 1000).toISOString() : null,
      });
      
      if (!tenant) {
        console.warn('⚠️ tenant_id is NULL in JWT! User needs to re-login to get updated token.');
      }
    } catch (error) {
      console.error('Error decoding JWT:', error);
      setUserRole(null);
      setTenantId(null);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      const token = session?.access_token ?? null;
      setAccessToken(token);
      decodeAndSetJwtClaims(token);
      
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        console.log('🔄 Auth state changed:', _event);
        setSession(session);
        setUser(session?.user ?? null);
        
        const token = session?.access_token ?? null;
        setAccessToken(token);
        decodeAndSetJwtClaims(token);
        
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unknown error occurred',
      };
    }
  };

  const signInWithAzure = async () => {
    try {
      // Keep it simple with just the required configuration
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'azure' as Provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback?next=/`,
        },
      });

      if (error) {
        console.error("Azure sign-in error:", error.message);
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Exception during Azure sign-in:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unknown error occurred',
      };
    }
  };

  const signUpWithAzure = async () => {
    // For OAuth providers like Azure, signup and signin use the same flow
    return signInWithAzure();
  };

  const signUp = async (_email: string, _password: string) => {
    return {
      success: false,
      error: 'Public sign up is disabled. Contact your administrator for an invitation.',
    };
  };

  const signOut = async () => {
    // scope: 'global' makes GoTrue revoke the refresh token server-side so a
    // subsequent login can't silently roll stale JWT claims forward (e.g.
    // after a tenant switch). Without this, signOut only clears localStorage
    // and the cached refresh_token can still mint fresh JWTs on the server.
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    if (error) {
      // Don't rethrow — the caller's UX (toast + redirect) should proceed
      // either way, and the in-memory session is already cleared. But log it
      // so we notice if server revocation is silently failing.
      console.warn('supabase.auth.signOut failed to revoke server-side:', error);
    }

    // Scrub app-specific caches that would otherwise resurrect stale
    // tenant / company / session state on the next login.
    try {
      localStorage.removeItem('chat:last-session-id');
      localStorage.removeItem('chat:active-company');
    } catch {
      // localStorage can throw in private-browsing / quota-exceeded edge cases.
    }
  };

  const value = {
    session,
    user,
    loading,
    accessToken,
    userRole,
    tenantId,
    signIn,
    signInWithAzure,
    signUpWithAzure,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
